import type { FC, ReactNode } from "react";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import {
  ActionButton,
  ActionButtonIntent,
  ActionButtonSize,
  Dialog,
  Icons,
  Item,
  LoadingSpinner,
  MenuOptionItem,
  MenuOptionItemIntent,
  MenuTriggerButton,
  TriggerButtonSize,
  VStack,
  useDialogWithPosition,
} from "widgets";

import { useError } from "@/app-utils/graphql/useError.ts";
import { getPlaybookEventResourcesSummary } from "@/app-utils/playbook.ts";
import type { EventResource, ProjectFile } from "@/graphql.ts";
import { getFilesApplicableToGenomeViewer } from "@/modules/files/utils/utils.ts";
import { getProjectFilesDataForSendingToGenomeViewer } from "@/redux/modules/files.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { EVENT_RESOURCE_TYPES } from "@/settings/settings.ts";
import BamFileValidationDialog from "@/ui-modules/files/BamFileValidationDialog.tsx";
import { PlaybookEventResource } from "@/ui-modules/playbooks/events/PlaybookEventResource.tsx";
import { useGetPlaybookEventOutputsCardLazyQuery } from "@/ui-modules/playbooks/events/graphql/event-queries.gql.ts";

export const EventResourcePopover: FC<{
  resourcesLabel: string;
  category: string;
  eventId: number;
}> = (props) => {
  const { state, triggerRef, arrowProps, dialogProps, dialogRef, triggerProps } = useDialogWithPosition({ offset: 8 });

  const [executeQuery, { data, loading, error }] = useGetPlaybookEventOutputsCardLazyQuery({
    variables: { eventId: props.eventId },
  });
  void useError(error);

  const resources = useMemo(() => {
    if (data?.playbook?.event?.results) {
      return Object.entries(getPlaybookEventResourcesSummary(data.playbook.event.results as EventResource[]))
        .filter(([categoryName]) => categoryName === props.category)
        .flatMap(([_, eventResource]) => eventResource);
    } else {
      return [];
    }
  }, [data]);

  const dispatch = useDispatch<ReduxDispatch>();

  const [projectFilesForGenomeViewer, setProjectFilesForGenomeViewer] = useState<ProjectFile[]>(null);

  const filesApplicableToGenomeViewer = getFilesApplicableToGenomeViewer(
    resources.filter((resource) => resource.file).flatMap((resource) => resource.file),
  );

  const cardMenuItems: ReactNode[] = [];
  if (filesApplicableToGenomeViewer.length) {
    cardMenuItems.push(
      <MenuTriggerButton
        icon={"more_vert"}
        size={TriggerButtonSize.Small}
        label={""}
        onAction={(key) => {
          switch (String(key)) {
            case "file-menu-genomeviewer":
              setProjectFilesForGenomeViewer(
                dispatch(
                  getProjectFilesDataForSendingToGenomeViewer(filesApplicableToGenomeViewer.map((file) => file.id)),
                ),
              );
              break;
          }
        }}
      >
        <Item key="file-menu-genomeviewer">
          <MenuOptionItem
            title="Send All to Genome Viewer"
            intent={MenuOptionItemIntent.Primary}
            icon={<img src={Icons.Action.GenomeViewer} width={20} height={20} />}
          />
        </Item>
      </MenuTriggerButton>,
    );
  }

  return (
    <>
      <ActionButton
        size={ActionButtonSize.InTableCell}
        intent={ActionButtonIntent.Outlined}
        aria-label={"Show more details"}
        label={props.resourcesLabel}
        ref={triggerRef}
        {...triggerProps}
        onPress={(event) => {
          void executeQuery();
          triggerProps.onPress?.(event);
        }}
      />
      {state.isOpen && (
        <Dialog
          closeFn={() => state.close()}
          domProps={dialogProps}
          arrowProps={arrowProps}
          ref={dialogRef}
          width={480}
          title={props.resourcesLabel}
          actions={cardMenuItems}
        >
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <VStack>
                {resources.map((resource, i) => (
                  <PlaybookEventResource
                    key={i}
                    eventResource={resource}
                    resourceCategory={
                      resource.resourceType === EVENT_RESOURCE_TYPES.UNKNOWN ? undefined : props.category
                    }
                  />
                ))}
              </VStack>

              {projectFilesForGenomeViewer && (
                <BamFileValidationDialog
                  key="file-bam-validation-dialog"
                  projectFiles={projectFilesForGenomeViewer}
                  onClose={() => {
                    setProjectFilesForGenomeViewer(null);
                  }}
                />
              )}
            </>
          )}
        </Dialog>
      )}
    </>
  );
};
